/**
 * BlockTransitionManager - Service for managing transitions within StrategyBlock objects
 *
 * Transitions (edges) are stored in block.action.edges[].
 *
 * This service provides a unified API analogous to ScreenTransitionManager:
 * - Extract all transitions from a block
 * - Add transitions to a block
 * - Update transitions in a block
 * - Remove transitions from a block
 *
 * Sprint: sprint-5 (FR-P4-004)
 * Template: ScreenTransitionManager.ts
 */

import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import { BLOCK_ACTION_KEY } from '@modules/reactFlow/constants/actionKeys';
import { getBlockEdgeTarget, type BlockEdgeLike } from '@modules/strategy-blocks/utils/blockNavigationEdges';

/**
 * Edge from OpenAPI schema (stored in Firebase)
 * Persisted strategy schema uses nextBlockId. Legacy AI-generated blocks may
 * still carry nextScreenId, so reads accept both.
 */
interface Edge {
  rule?: Array<{
    key?: string;
    operator?: string;
    value?: string;
  }>;
  nextBlockId?: string;
  nextScreenId?: string;
}

/**
 * Service for managing transitions within StrategyBlock objects
 */
export class BlockTransitionManager {
  /**
   * Extract all transitions from a block
   *
   * Searches block.action.edges[] for edges with nextBlockId.
   *
   * @param block - StrategyBlock object
   * @returns Array of transitions found in the block
   */
  extractTransitions(block: StrategyBlock): EntityTransition[] {
    if (!block.entityId) {
      console.error(`[BlockTransitionManager] ❌ CRITICAL: Block ${block.id} missing entityId`);
      throw new Error(`Block ${block.id} missing entityId - cannot extract transitions`);
    }

    const transitions: EntityTransition[] = [];
    const blockId = block.entityId;

    console.log(`[BlockTransitionManager] extractTransitions - blockId: ${blockId}`);

    // Extract from single location: block.action.edges[]
    const edges = block.action?.edges;

    if (edges && Array.isArray(edges)) {
      edges.forEach((edge) => {
        const targetBlockId = getBlockEdgeTarget(edge as BlockEdgeLike);
        if (targetBlockId) {
          const transition = {
            id: TransitionIdGenerator.generate(blockId, BLOCK_ACTION_KEY, targetBlockId),
            sourceEntityId: blockId,
            targetEntityId: targetBlockId,
            sourceActionKey: BLOCK_ACTION_KEY,
            label: undefined, // Edges don't have labels in schema
          };

          // DEBUG: Show created transition
          console.log(`[BlockTransitionManager] Created transition: source=${transition.sourceEntityId}, target=${transition.targetEntityId}`);

          transitions.push(transition);
        }
      });
    }

    return transitions;
  }

  /**
   * Add a transition to a block
   *
   * Creates the necessary action structure if it doesn't exist.
   * Returns a new block object (immutable update).
   *
   * @param block - Original block
   * @param transition - Transition to add
   * @returns Updated block with the new transition
   */
  addTransition(block: StrategyBlock, transition: EntityTransition): StrategyBlock {
    const newBlock = JSON.parse(JSON.stringify(block)); // Deep clone (immutability)

    // Ensure action exists
    if (!newBlock.action) {
      newBlock.action = {};
    }
    if (!newBlock.action.edges) {
      newBlock.action.edges = [];
    }

    // Create edge object
    const edge: Edge = {
      nextBlockId: transition.targetEntityId,
      rule: [], // Empty rules for now (analogous to Screen edges)
    };

    // Add to block.action.edges[]
    newBlock.action.edges.push(edge);

    return newBlock;
  }

  /**
   * Update a transition in a block
   *
   * Finds the edge by transition ID and updates its properties.
   * Returns a new block object (immutable update).
   *
   * @param block - Original block
   * @param transitionId - Transition ID to update
   * @param updates - Partial transition updates
   * @returns Updated block with modified transition
   */
  updateTransition(
    block: StrategyBlock,
    transitionId: string,
    updates: Partial<EntityTransition>
  ): StrategyBlock {
    const newBlock = JSON.parse(JSON.stringify(block)); // Deep clone

    // Parse transition ID
    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch (error) {
      console.warn(`[BlockTransitionManager] Invalid transition ID format: ${transitionId}`, error);
      return block;
    }

    const { targetEntityId: oldTargetBlockId } = parsed;

    // Find edge in block.action.edges[]
    const edges = newBlock.action?.edges;
    if (!edges || !Array.isArray(edges)) {
      console.warn(`[BlockTransitionManager] No edges found in block ${block.id}`);
      return block;
    }

    const edgeIndex = edges.findIndex(e => getBlockEdgeTarget(e) === oldTargetBlockId);
    if (edgeIndex === -1) {
      console.warn(`[BlockTransitionManager] Edge not found: ${transitionId}`);
      return block;
    }

    // Update edge
    if (updates.targetEntityId !== undefined) {
      edges[edgeIndex].nextBlockId = updates.targetEntityId;
      delete edges[edgeIndex].nextScreenId;
    }

    return newBlock;
  }

  /**
   * Remove a transition from a block
   *
   * Finds the edge by transition ID and removes it from the array.
   * Returns a new block object (immutable update).
   *
   * @param block - Original block
   * @param transitionId - Transition ID to remove
   * @returns Updated block without the transition
   */
  removeTransition(block: StrategyBlock, transitionId: string): StrategyBlock {
    const newBlock = JSON.parse(JSON.stringify(block)); // Deep clone

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch (error) {
      console.warn(`[BlockTransitionManager] Invalid transition ID format: ${transitionId}`, error);
      return block;
    }

    const { targetEntityId: targetBlockId } = parsed;

    const edges = newBlock.action?.edges;
    if (!edges || !Array.isArray(edges)) {
      console.warn(`[BlockTransitionManager] No edges found in block ${block.id}`);
      return block;
    }

    const edgeIndex = edges.findIndex(e => getBlockEdgeTarget(e) === targetBlockId);

    if (edgeIndex !== -1) {
      edges.splice(edgeIndex, 1);
    } else {
      console.warn(`[BlockTransitionManager] Edge not found: ${transitionId}`);
    }

    return newBlock;
  }
}

// Singleton instance
export const blockTransitionManager = new BlockTransitionManager();
